<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { logoutHelper } from '@/auth/utils'
import { useAuthStore } from '@/stores/auth'
import { useWorkspaceStore } from '@/stores/workspace'
import { workspaceDisplayLabel } from '@/utils/workspace-label'

const emit = defineEmits<{ (e: 'close'): void }>()

const { t } = useI18n()
const toast = useToast()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()

const userEmail = computed(() => authStore.currentUser?.email || '')
const activeWorkspace = computed(() => workspaceStore.activeWorkspace)
const allWorkspaces = computed(() => workspaceStore.workspaces || [])
const myJoinRequests = computed(() => workspaceStore.myJoinRequests || [])

const activeWorkspaceLabel = computed(() => workspaceDisplayLabel(activeWorkspace.value, userEmail.value))

const otherWorkspaces = computed(() =>
  allWorkspaces.value.filter(w => w.id !== activeWorkspace.value?.id),
)

const pendingJoinRequests = computed(
  () => myJoinRequests.value.filter(r => r.status === 'pending'),
)

async function onSwitchWorkspace(workspaceId: number) {
  emit('close')
  if (workspaceId === activeWorkspace.value?.id)
    return

  // Look up the target name now (before the active-workspace store
  // mutation flips) so the toast can name it explicitly.
  const target = allWorkspaces.value.find(w => w.id === workspaceId)
  const targetLabel = workspaceDisplayLabel(target, userEmail.value)
  try {
    await workspaceStore.switchWorkspace(workspaceId)

    // Refresh the active workspace + list so any pages currently
    // mounted (Orders, Invoices, Account) re-render against the new
    // active workspace instead of stale data.
    await workspaceStore.fetchActiveWorkspace()

    // Default landing on every workspace switch: Orders. Orders is the
    // only tab guaranteed to render in any workspace (it falls back to
    // local IC data when the workspace isn't Swida-linked), so we always
    // bounce there to avoid stranding the user on a tab the new workspace
    // doesn't expose.
    if (route.path.startsWith('/workspace/') && route.path !== '/workspace/orders')
      await router.push('/workspace/orders')

    // A name ending in a dot (ACME s.r.o.) also ends the sentence: no second dot.
    toast.success(
      targetLabel
        ? t('Switched to {name}.', { name: targetLabel }).replace(/\.\.$/, '.')
        : t('Workspace switched.'),
    )
  }
  catch (error: unknown) {
    console.error(error)

    const err = error as { response?: { data?: { detail?: string } } }

    toast.error(
      err?.response?.data?.detail || t('Could not switch workspace.'),
    )
  }
}

function signOut() {
  emit('close')
  logoutHelper()
}
</script>

<template>
  <div class="account-menu">
    <div v-if="activeWorkspace" class="account-menu-workspace">
      <div class="ws-menu-section-label">
        {{ $t('Current') }}
      </div>
      <div class="ws-menu-current">
        <span class="ws-menu-dot ws-menu-dot--filled" />
        <span class="ws-menu-name">{{ activeWorkspaceLabel }}</span>
        <span
          v-if="activeWorkspace.workspace_type === 'personal'"
          class="ws-menu-badge ws-menu-badge--personal"
        >
          {{ $t('Personal') }}
        </span>
        <span
          v-else-if="activeWorkspace.is_verified"
          class="ws-menu-badge ws-menu-badge--verified"
        >
          {{ $t('Verified') }}
        </span>
        <span
          v-else-if="activeWorkspace.has_pending_verification"
          class="ws-menu-badge ws-menu-badge--pending-verification"
        >
          {{ $t('Verification pending') }}
        </span>
      </div>

      <template v-if="otherWorkspaces.length > 0">
        <div class="ws-menu-section-label">
          {{ $t('Switch to') }}
        </div>
        <button
          v-for="ws in otherWorkspaces"
          :key="ws.id"
          type="button"
          class="ws-menu-row"
          @click="onSwitchWorkspace(ws.id)"
        >
          <span class="ws-menu-dot" />
          <span class="ws-menu-name">{{ workspaceDisplayLabel(ws, userEmail) }}</span>
          <span
            v-if="ws.workspace_type === 'personal'"
            class="ws-menu-badge ws-menu-badge--personal"
          >
            {{ $t('Personal') }}
          </span>
          <span
            v-else-if="ws.is_verified"
            class="ws-menu-badge ws-menu-badge--verified"
          >
            {{ $t('Verified') }}
          </span>
          <span
            v-else-if="ws.has_pending_verification"
            class="ws-menu-badge ws-menu-badge--pending-verification"
          >
            {{ $t('Verification pending') }}
          </span>
        </button>
      </template>

      <template v-if="pendingJoinRequests.length > 0">
        <div class="ws-menu-section-label">
          {{ $t('Pending') }}
        </div>
        <div
          v-for="req in pendingJoinRequests"
          :key="`req-${req.id}`"
          class="ws-menu-row ws-menu-row--pending"
          :title="$t('Waiting for the workspace admin to approve your join request.')"
        >
          <span class="ws-menu-dot ws-menu-dot--pending" />
          <span class="ws-menu-name">{{ req.workspace_name }}</span>
          <span class="ws-menu-badge ws-menu-badge--waiting">
            {{ $t('Waiting') }}
          </span>
        </div>
      </template>

      <RouterLink
        class="ws-menu-row ws-menu-row--manage"
        :to="{ path: '/account', query: { form: 'company' } }"
        @click="emit('close')"
      >
        {{ $t('Manage workspaces') }} →
      </RouterLink>

      <div class="account-menu-divider" />
    </div>

    <RouterLink
      class="account-menu-item"
      :to="{ path: '/account', query: { form: 'general' } }"
      @click="emit('close')"
    >
      {{ $t('Account settings') }}
    </RouterLink>
    <RouterLink
      class="account-menu-item"
      :to="{ path: '/account', query: { form: 'password' } }"
      @click="emit('close')"
    >
      {{ $t('Change password') }}
    </RouterLink>
    <button
      type="button"
      class="account-menu-item"
      @click="signOut"
    >
      {{ $t('Sign Out') }}
    </button>
  </div>
</template>

<style scoped>
.account-menu {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-family: 'IBM Plex Sans', sans-serif;
  text-align: left;
}

.account-menu-workspace {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.account-menu-divider {
  height: 1px;
  margin: 6px 4px;
  background-color: #E2E3E9;
}

.account-menu-item {
  display: flex;
  align-items: center;
  width: 100%;
  margin: 0;
  padding: 0.35rem 0.25rem;
  border: 0;
  background: transparent;
  color: #0E0F10 !important;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  text-align: left;
  text-decoration: none !important;
  cursor: pointer;
  transition: color 0.15s;
}

.account-menu-item:hover,
.account-menu-item:focus-visible {
  color: #D81B5D !important;
}

.ws-menu-section-label {
  padding: 6px 10px 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #9CA3AF;
}

.ws-menu-current,
.ws-menu-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin: 0;
  padding: 8px 10px;
  border: 0;
  background: transparent;
  border-radius: 8px;
  font-size: 13px;
  color: #18191F;
  text-align: left;
  cursor: pointer;
  text-decoration: none !important;
  transition: background-color 0.1s;
}

.ws-menu-current {
  background-color: #FFF5F8;
  cursor: default;
}

.ws-menu-row:hover {
  background-color: #F1F2F6;
}

.ws-menu-row--pending {
  cursor: default;
}

.ws-menu-row--pending:hover {
  background-color: transparent;
}

.ws-menu-row--manage {
  color: #D81B5D !important;
  font-weight: 600;
}

.ws-menu-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 1px solid #C9CED6;
  background-color: transparent;
}

.ws-menu-dot--filled {
  background-color: #D81B5D;
  border-color: #D81B5D;
}

.ws-menu-dot--pending {
  background-color: #FCD34D;
  border-color: #F59E0B;
}

.ws-menu-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
  color: #18191F !important;
}

.ws-menu-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}

.ws-menu-badge--personal {
  background-color: #E5E8ED;
  color: #626873;
}

.ws-menu-badge--verified {
  background-color: #DEF7EC;
  color: #047857;
}

.ws-menu-badge--pending-verification {
  background-color: #FEF3C7;
  color: #92400E;
}

.ws-menu-badge--waiting {
  background-color: #FFF8E5;
  color: #8A6D00;
}
</style>
